import { DropdownItem, DropdownValue } from '@bybeam/platform-types';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import useHelperTextId from '@platform-ui-common/hooks/contexts/useHelperTextId.js';
import { parseHTML } from '@platform-ui-common/utilities/parseHTML.js';
import colors from '@platform-ui/styles/base-colors.theme.js';
import { getLabelProps } from '@platform-ui/utilities/application/utils.js';
import cx from 'classnames';
import { useMemo } from 'react';
import { DropdownProps, SingleSelectDropdownProps } from './types.js';
import {
  convertValuesToLabel,
  filterVisibleItems,
  mapItemsToParents,
  removeHTML,
} from './utils.js';

export default function Dropdown(props: DropdownProps): JSX.Element {
  const { disabled = false, error, fullWidth, required = false } = props;

  return (
    <div className={cx('max-w-full', !fullWidth && 'w-field')}>
      <FormControl variant="filled" required={required} disabled={disabled} error={!!error}>
        {'searchable' in props && props.searchable ? (
          <SearchableDropdown {...props} />
        ) : (
          <NonSearchableDropdown {...props} />
        )}
      </FormControl>
    </div>
  );
}

function SearchableDropdown({
  error,
  helperText,
  id,
  items,
  label,
  onChange,
  required,
  value,
}: SingleSelectDropdownProps): JSX.Element {
  const handleAutocompleteChange = (_, option) => {
    onChange(option?.value);
  };
  const additionalText = typeof error === 'string' ? error : helperText;
  const helperTextId = useHelperTextId();
  return (
    <>
      <Autocomplete
        autoSelect={false}
        id={`searchable-dropdown-${id}`}
        options={items}
        value={value}
        getOptionLabel={(option) => removeHTML(option.label || convertValuesToLabel(option, items))}
        renderOption={(props, option) => (
          <li {...props}>
            <span>{parseHTML(option.label)}</span>
          </li>
        )}
        isOptionEqualToValue={(option) => option.value === value}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              'aria-errormessage': helperTextId,
              'aria-invalid': !!error,
              'aria-describedby': helperTextId,
            }}
            error={!!error}
            label={`${label} ${required ? '*' : ''}`}
            variant="filled"
          />
        )}
        onChange={handleAutocompleteChange}
        getOptionDisabled={(option) => option.disabled}
      />
      {additionalText && <FormHelperText id={helperTextId}>{additionalText}</FormHelperText>}
    </>
  );
}

function NonSearchableDropdown(props: DropdownProps): JSX.Element {
  const {
    autoComplete,
    error,
    helperText,
    id,
    inputProps,
    items,
    label,
    multiple,
    onChange,
    placeholder,
    value,
    selectAll,
    ...rest
  } = props;

  const childMode = 'childMode' in props ? props.childMode : 'OnParentSelect';
  const visibleItems = useMemo(
    () => filterVisibleItems(items, Array.isArray(value) ? value : [value], childMode),
    [items, value, childMode],
  );
  const allSelected = Array.isArray(value) && value.length === visibleItems.length;
  const helperTextId = useHelperTextId();
  const handleChange = (event: SelectChangeEvent<DropdownValue | DropdownValue[]>): void => {
    let newSelection = event.target.value;
    if (Array.isArray(newSelection) && newSelection.includes('selectAll')) {
      handleSelectAll(!allSelected);
    } else {
      if (Array.isArray(newSelection) && childMode === 'OnParentSelect') {
        const mappedItems = mapItemsToParents(items);
        newSelection = newSelection.filter((v) => {
          const parent = mappedItems.find((item) => item.value === v)?.parent;
          return !parent || (newSelection as DropdownValue[]).includes(parent);
        });
      }
      onChange(newSelection);
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      onChange(mapItemsToParents(visibleItems).map((item) => item.value));
    } else {
      onChange([]);
    }
  };

  const additionalText = typeof error === 'string' ? error : helperText;
  const selectAllStyles = allSelected
    ? {
        backgroundColor: colors.text,
        color: colors.white,
      }
    : {};

  const parsedValues = multiple ? [value].flat().map((value) => value.toString()) : value;
  return (
    <>
      <InputLabel id={`label-${id}`} {...getLabelProps(!!inputProps?.readOnly, value)}>
        {label}
      </InputLabel>
      <Select
        {...rest}
        labelId={`label-${id}`}
        id={`dropdown-${id}`}
        value={parsedValues}
        multiple={multiple}
        onChange={handleChange}
        label={label}
        IconComponent={ExpandMoreIcon}
        autoComplete={autoComplete}
        aria-describedby={helperTextId}
        inputProps={inputProps}
        renderValue={(selected: DropdownValue | DropdownValue[]) => (
          <span>{parseHTML(convertValuesToLabel(selected, items))}</span>
        )}
        MenuProps={{ className: 'max-h-[80%] md:max-h-full' }}
      >
        {placeholder && <MenuItem disabled>{placeholder}</MenuItem>}
        {selectAll && (
          <MenuItem value="selectAll" style={selectAllStyles}>
            <ListItemIcon>
              <Checkbox checked={allSelected} color="primary" style={{ marginTop: 0 }} />
            </ListItemIcon>
            <ListItemText primary="Select All" sx={{ textWrap: 'wrap' }} />
          </MenuItem>
        )}
        {visibleItems.map((option: DropdownItem & { isChild?: boolean }) => (
          <MenuItem
            key={`${option.value?.toString()}`}
            value={option.value}
            disabled={option.disabled}
            className={cx({ '!pl-8': !!option.isChild })}
          >
            {props.multiple && (
              <Checkbox
                checked={
                  Array.isArray(parsedValues)
                    ? parsedValues?.includes(option.value)
                    : String(parsedValues) === option.value.toString()
                }
                color="primary"
                style={{ marginTop: 0 }}
              />
            )}
            <ListItemText
              primary={<span>{parseHTML(option.label)}</span>}
              secondary={option.secondaryLabel}
              sx={{ textWrap: 'wrap' }}
            />
          </MenuItem>
        ))}
      </Select>
      {additionalText && <FormHelperText id={helperTextId}>{additionalText}</FormHelperText>}
    </>
  );
}
